import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { BrowserProvider, Signer } from 'ethers';

// ---- chain config -------------------------------------------------------

import { useNetwork } from './NetworkContext';

// Build-time fallback only — the runtime network selection (NetworkContext) wins.
const ENV_REQUIRED_CHAIN_ID = Number(
  import.meta.env.VITE_BLOCKCHAIN_CHAIN_ID ?? 99999,
);

const CHAIN_PARAMS: Record<number, object> = {
  36900: {
    chainId: '0x9024', // 36900
    chainName: 'ADI Mainnet',
    nativeCurrency: { name: 'ADI', symbol: 'ADI', decimals: 18 },
    rpcUrls: [import.meta.env.VITE_BLOCKCHAIN_RPC_URL ?? 'https://rpc.adifoundation.ai'],
    blockExplorerUrls: [import.meta.env.VITE_BLOCK_EXPLORER_URL ?? 'https://explorer.adifoundation.ai'],
  },
  99999: {
    chainId: '0x1869F', // 99999
    chainName: 'ADI Testnet',
    nativeCurrency: { name: 'ADI', symbol: 'ADI', decimals: 18 },
    rpcUrls: [import.meta.env.VITE_BLOCKCHAIN_RPC_URL ?? 'https://rpc.ab.testnet.adifoundation.ai'],
    blockExplorerUrls: ['https://explorer.ab.testnet.adifoundation.ai'],
  },
  8400: {
    chainId: '0x20D0', // 8400
    chainName: 'ADI Mainnet',
    nativeCurrency: { name: 'ADI', symbol: 'ADI', decimals: 18 },
    rpcUrls: [import.meta.env.VITE_BLOCKCHAIN_RPC_URL ?? 'https://rpc.ab.adifoundation.ai'],
    blockExplorerUrls: ['https://explorer.ab.adifoundation.ai'],
  },
  31337: {
    chainId: '0x7A69', // 31337
    chainName: 'Hardhat (local)',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['http://127.0.0.1:8545'],
    blockExplorerUrls: [],
  },
};

// ---- types --------------------------------------------------------------

export interface Web3ContextValue {
  account: string | null;
  chainId: number | null;
  isConnected: boolean;
  isConnecting: boolean;
  isWrongNetwork: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  switchNetwork: () => Promise<void>;
  provider: BrowserProvider | null;
  signer: Signer | null;
  error: string | null;
}

// ---- context ------------------------------------------------------------

const Web3Context = createContext<Web3ContextValue | null>(null);

export function useWeb3(): Web3ContextValue {
  const ctx = useContext(Web3Context);
  if (!ctx) throw new Error('useWeb3 must be used inside Web3Provider');
  return ctx;
}

// ---- provider -----------------------------------------------------------

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ethereum?: any;
  }
}

export function Web3Provider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signer, setSigner] = useState<Signer | null>(null);
  const providerRef = useRef<BrowserProvider | null>(null);

  const isConnected = account !== null;
  // Multi-chain: the wallet must be on the network the USER SELECTED in the
  // header, not the build-time env chain.
  const { selected } = useNetwork();
  const REQUIRED_CHAIN_ID = selected?.chainId ?? ENV_REQUIRED_CHAIN_ID;
  const isWrongNetwork = chainId !== null && chainId !== REQUIRED_CHAIN_ID;

  // Re-build provider/signer whenever MetaMask state changes.
  const refreshSigner = useCallback(async () => {
    if (!window.ethereum) return;
    const provider = new BrowserProvider(window.ethereum);
    providerRef.current = provider;
    try {
      const s = await provider.getSigner();
      setSigner(s);
    } catch {
      setSigner(null);
    }
  }, []);

  // On mount: if MetaMask already has an account connected (e.g. page reload),
  // restore state without prompting the user.
  useEffect(() => {
    if (!window.ethereum) return;

    const init = async () => {
      try {
        const accounts: string[] = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          const chainHex: string = await window.ethereum.request({ method: 'eth_chainId' });
          setAccount(accounts[0]);
          setChainId(parseInt(chainHex, 16));
          await refreshSigner();
        }
      } catch {
        // MetaMask not yet unlocked — fine, stay disconnected.
      }
    };
    init();

    const onAccountsChanged = async (accounts: string[]) => {
      if (accounts.length === 0) {
        setAccount(null);
        setSigner(null);
      } else {
        setAccount(accounts[0]);
        await refreshSigner();
      }
    };
    const onChainChanged = async (chainHex: string) => {
      setChainId(parseInt(chainHex, 16));
      await refreshSigner();
    };

    window.ethereum.on('accountsChanged', onAccountsChanged);
    window.ethereum.on('chainChanged', onChainChanged);
    return () => {
      window.ethereum?.removeListener('accountsChanged', onAccountsChanged);
      window.ethereum?.removeListener('chainChanged', onChainChanged);
    };
  }, [refreshSigner]);

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      setError('MetaMask is not installed. Install it from metamask.io');
      return;
    }
    setIsConnecting(true);
    setError(null);
    try {
      const accounts: string[] = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });
      const chainHex: string = await window.ethereum.request({ method: 'eth_chainId' });
      setAccount(accounts[0]);
      setChainId(parseInt(chainHex, 16));
      await refreshSigner();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Connection rejected';
      setError(msg);
    } finally {
      setIsConnecting(false);
    }
  }, [refreshSigner]);

  const disconnect = useCallback(() => {
    setAccount(null);
    setChainId(null);
    setSigner(null);
    providerRef.current = null;
  }, []);

  const switchNetwork = useCallback(async () => {
    if (!window.ethereum) return;
    const chainHex = `0x${REQUIRED_CHAIN_ID.toString(16)}`;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainHex }],
      });
    } catch (switchErr: unknown) {
      // 4902 = chain not added yet — add it first then switch.
      if ((switchErr as { code?: number }).code === 4902) {
        // Prefer the runtime registry entry; fall back to the legacy map.
        const params = selected
          ? {
              chainId: chainHex,
              chainName: selected.name,
              nativeCurrency: selected.nativeCurrency,
              rpcUrls: selected.rpcUrls,
              blockExplorerUrls: selected.explorer ? [selected.explorer] : [],
            }
          : CHAIN_PARAMS[REQUIRED_CHAIN_ID];
        if (params) {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [params],
          });
        }
      } else {
        throw switchErr;
      }
    }
  }, [REQUIRED_CHAIN_ID, selected]);

  return (
    <Web3Context.Provider
      value={{
        account,
        chainId,
        isConnected,
        isConnecting,
        isWrongNetwork,
        connect,
        disconnect,
        switchNetwork,
        provider: providerRef.current,
        signer,
        error,
      }}
    >
      {children}
    </Web3Context.Provider>
  );
}
